import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Inbox, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { dirOf } from '@/lib/i18n';
import { setupApi, type AcceptResult, type Suggestion } from './setup-api';

type Kind = Suggestion['kind'];
type Patch = { payload?: Record<string, unknown>; status?: 'PENDING' | 'REJECTED' };

/**
 * מאיפה ההצעות ומה קורה בסיום. ברירת המחדל — אשף ההצטרפות (/onboarding-import).
 * "למידה ממסמכים" (features/learning) מעבירה ריצה מסוימת דרך /learning.
 */
export interface ReviewSource {
  queryKey: readonly unknown[];
  list: (signal?: AbortSignal) => Promise<Suggestion[]>;
  update: (id: string, patch: Patch) => Promise<unknown>;
  accept: () => Promise<AcceptResult>;
  /** סיום: מה שלא אושר נמחק. */
  finish: () => Promise<unknown>;
  onDone: () => void | Promise<void>;
}

function useOnboardingSource(): ReviewSource {
  // בלי אזהרה: הרכיב מרונדר גם בבדיקות בלי RouterProvider.
  const router = useRouter({ warn: false });
  return useMemo(
    () => ({
      queryKey: ['onboarding-import', 'suggestions'],
      list: setupApi.suggestions,
      update: setupApi.update,
      accept: setupApi.accept,
      finish: setupApi.finish,
      onDone: async () => {
        await router?.navigate({ to: '/', replace: true });
      },
    }),
    [router],
  );
}

/**
 * מסך האישור. כל שורה מסומנת כברירת מחדל — הבעלים מוריד את מה שלא
 * שייך ומתקן מה שצריך, ולוחץ פעם אחת. זה מהיר יותר מאישור שורה-שורה,
 * ועדיין שום דבר לא נכתב לפני הלחיצה.
 */
export function ImportReview({
  source,
  skipLabel,
  doneTitle,
}: {
  /** בלי — אשף ההצטרפות. */
  source?: ReviewSource;
  skipLabel?: string;
  /** כותרת ההודעה אחרי ההוספה ("העסק מוכן" באשף). */
  doneTitle?: string;
} = {}) {
  const { t } = useTranslation(['onboarding', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const onboarding = useOnboardingSource();
  const src = source ?? onboarding;
  const SUGGESTIONS_KEY = src.queryKey;

  const list = useQuery({
    queryKey: SUGGESTIONS_KEY,
    queryFn: ({ signal }) => src.list(signal),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Patch }) => src.update(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUGGESTIONS_KEY }),
    onError: (err: unknown) => {
      toast.error(t('review.changeFailed'), err instanceof ApiError ? err.message : undefined);
      void qc.invalidateQueries({ queryKey: SUGGESTIONS_KEY });
    },
  });

  const finish = useMutation({
    mutationFn: async (acceptFirst: boolean) => {
      const result = acceptFirst ? await src.accept() : null;
      await src.finish();
      return result;
    },
    onSuccess: async (result) => {
      if (result) {
        toast.success(
          doneTitle ?? t('review.ready'),
          [
            t('review.added', { count: result.accepted }),
            result.failed.length ? t('review.notAdded', { count: result.failed.length }) : null,
          ]
            .filter(Boolean)
            .join(', '),
        );
      }
      await qc.invalidateQueries();
      await src.onDone();
    },
    onError: (err: unknown) =>
      toast.error(t('common:errors.saveFailed'), err instanceof ApiError ? err.message : undefined),
  });

  if (list.isPending) return <Skeleton className="h-96" />;
  if (list.isError) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;

  const byKind = (kind: Kind) => list.data.filter((s) => s.kind === kind);
  const included = list.data.filter((s) => s.status === 'PENDING').length;
  const toggle = (s: Suggestion) =>
    update.mutate({ id: s.id, patch: { status: s.status === 'PENDING' ? 'REJECTED' : 'PENDING' } });
  const save = (s: Suggestion, payload: Record<string, unknown>) =>
    update.mutate({ id: s.id, patch: { payload } });

  const tabs: Array<{ kind: Kind; label: string }> = [
    { kind: 'CUSTOMER', label: t('review.tabs.customers') },
    { kind: 'PRICE_ITEM', label: t('review.tabs.priceList') },
    { kind: 'JOB_TYPE', label: t('review.tabs.jobTypes') },
    { kind: 'SUPPLIER', label: t('review.tabs.suppliers') },
    { kind: 'MATERIAL', label: t('review.tabs.materials') },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('review.title')}</CardTitle>
        <CardDescription>
          {t('review.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {list.data.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title={t('review.emptyTitle')}
            description={t('review.emptyDescription')}
          />
        ) : (
          <Tabs
            defaultValue={tabs.find((tab) => byKind(tab.kind).length > 0)?.kind ?? 'CUSTOMER'}
            dir={dirOf()}
          >
            <TabsList>
              {tabs.map((tab) => (
                <TabsTrigger key={tab.kind} value={tab.kind} className="gap-2">
                  {tab.label}
                  <Badge dot={false}>
                    {byKind(tab.kind).filter((s) => s.status === 'PENDING').length}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map((tab) => (
              <TabsContent key={tab.kind} value={tab.kind}>
                {byKind(tab.kind).length === 0 ? (
                  <p className="py-8 text-center text-sm text-fg-muted">{t('review.emptyCategory')}</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {byKind(tab.kind).map((s) => (
                      <SuggestionRow
                        key={s.id}
                        suggestion={s}
                        onToggle={() => toggle(s)}
                        onSave={(p) => save(s, p)}
                      />
                    ))}
                  </ul>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}

        <div className="mt-6 flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-between">
          <Button variant="ghost" disabled={finish.isPending} onClick={() => finish.mutate(false)}>
            {skipLabel ?? t('review.skip')}
          </Button>
          <Button
            size="lg"
            loading={finish.isPending}
            disabled={included === 0 || update.isPending}
            onClick={() => finish.mutate(true)}
          >
            {t('review.addItems', { count: included })}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SuggestionRow({
  suggestion: s,
  onToggle,
  onSave,
}: {
  suggestion: Suggestion;
  onToggle: () => void;
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const { t } = useTranslation('onboarding');
  const on = s.status === 'PENDING';
  const checkboxId = `sg-${s.id}`;

  return (
    <li className={on ? 'py-3' : 'py-3 opacity-55'}>
      <div className="flex items-start gap-3">
        <Checkbox
          id={checkboxId}
          checked={on}
          onCheckedChange={onToggle}
          className="mt-3"
          aria-label={t('review.include')}
        />
        <div className="min-w-0 flex-1">
          {s.kind === 'CUSTOMER' ? (
            <div className="grid gap-2 sm:grid-cols-[1fr_14rem] sm:items-center">
              <InlineText
                label={t('review.customerName')}
                value={s.payload.name}
                disabled={!on}
                onCommit={(name) => onSave({ name })}
              />
              <span dir="ltr" className="truncate text-end text-xs text-fg-muted">
                {s.payload.email ?? s.payload.phone ?? ''}
              </span>
              <span className="flex flex-wrap items-center gap-1.5 text-2xs text-fg-subtle sm:col-span-2">
                {s.payload.existingId ? <UpdateBadge /> : null}
                {s.payload.existingId
                  ? t('review.fillsDetails', {
                      fields: [
                        s.payload.phone ? t('review.fields.phone') : null,
                        s.payload.email ? t('review.fields.email') : null,
                        s.payload.taxId ? t('review.fields.taxId') : null,
                        s.payload.address ? t('review.fields.address') : null,
                      ]
                        .filter(Boolean)
                        .join(', '),
                    })
                  : s.payload.greenInvoiceId
                    ? t('review.fromGreenInvoice', { count: s.payload.messageCount })
                    : s.payload.documentCount > 0
                      ? t('review.foundIn', { count: s.payload.documentCount })
                      : t('review.emails', { count: s.payload.messageCount })}
                {s.payload.taxId && !s.payload.existingId
                  ? ` · ${t('review.companyId', { id: s.payload.taxId })}`
                  : ''}
              </span>
            </div>
          ) : s.kind === 'PRICE_ITEM' ? (
            <div className="grid gap-2 sm:grid-cols-[1fr_8rem] sm:items-center">
              <InlineText
                label={t('review.itemDescription')}
                value={s.payload.description}
                // עדכון מחיר לפריט קיים — התיאור שלו לא משתנה מכאן.
                disabled={!on || !!s.payload.existingId}
                onCommit={(description) => onSave({ description })}
              />
              <InlinePrice
                value={s.payload.price}
                disabled={!on}
                onCommit={(price) => onSave({ price })}
              />
              <span className="flex flex-wrap items-center gap-1.5 text-2xs text-fg-subtle sm:col-span-2">
                {s.payload.existingId ? <UpdateBadge /> : null}
                {s.payload.existingId && s.payload.currentPrice != null
                  ? `${t('review.currentPrice', { price: s.payload.currentPrice })} · `
                  : ''}
                {t('review.foundIn', { count: s.evidence })}
                {s.payload.unit ? ` · ${t('review.perUnit', { unit: s.payload.unit })}` : ''}
                {s.payload.minPrice !== null &&
                s.payload.maxPrice !== null &&
                s.payload.minPrice !== s.payload.maxPrice
                  ? ` · ${t('review.range', { min: s.payload.minPrice, max: s.payload.maxPrice })}`
                  : ''}
              </span>
            </div>
          ) : s.kind === 'SUPPLIER' ? (
            <div className="grid gap-2 sm:grid-cols-[1fr_14rem] sm:items-center">
              <InlineText
                label={t('review.supplierName')}
                value={s.payload.name}
                disabled={!on}
                onCommit={(name) => onSave({ name })}
              />
              <span dir="ltr" className="truncate text-end text-xs text-fg-muted">
                {s.payload.email ?? s.payload.phone ?? ''}
              </span>
              <span className="text-2xs text-fg-subtle sm:col-span-2">
                {t('review.expenses', { count: s.evidence })}
                {s.payload.taxId ? ` · ${t('review.companyId', { id: s.payload.taxId })}` : ''}
              </span>
            </div>
          ) : s.kind === 'MATERIAL' ? (
            <div className="grid gap-2 sm:grid-cols-[1fr_8rem] sm:items-center">
              <InlineText
                label={t('review.itemDescription')}
                value={s.payload.description}
                disabled={!on}
                onCommit={(description) => onSave({ description })}
              />
              <InlinePrice
                value={s.payload.price}
                disabled={!on}
                onCommit={(price) => onSave({ price })}
              />
              <span className="flex flex-wrap items-center gap-1.5 text-2xs text-fg-subtle sm:col-span-2">
                <Badge dot={false} tone={s.payload.category === 'HARDWARE' ? 'info' : 'neutral'}>
                  {s.payload.category === 'HARDWARE' ? t('review.hardware') : t('review.material')}
                </Badge>
                {t('review.cost', { cost: s.payload.cost })}
                {s.payload.supplierName ? ` · ${s.payload.supplierName}` : ''}
                {` · ${t('review.bought', { count: s.evidence })} · ${t('review.priceIsCost')}`}
              </span>
            </div>
          ) : (
            <div className="space-y-1.5">
              <InlineText
                label={t('review.jobTypeName')}
                value={s.payload.name}
                disabled={!on}
                onCommit={(name) => onSave({ name })}
              />
              {s.payload.defaultChecklist.length > 0 ? (
                <p className="text-xs text-fg-muted">
                  {s.payload.defaultChecklist.map((c) => c.label).join(' · ')}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function UpdateBadge() {
  const { t } = useTranslation('onboarding');
  return (
    <Badge dot={false} tone="info" className="gap-1">
      <RefreshCw className="size-3" aria-hidden />
      {t('review.updatesExisting')}
    </Badge>
  );
}

/** שדה שנשמר ביציאה ממנו, ורק אם השתנה. */
function InlineText({
  label,
  value,
  disabled,
  onCommit,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <Input
      aria-label={label}
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim();
        if (next && next !== value) onCommit(next);
        else setDraft(value);
      }}
    />
  );
}

function InlinePrice({
  value,
  disabled,
  onCommit,
}: {
  value: number;
  disabled: boolean;
  onCommit: (v: number) => void;
}) {
  const { t } = useTranslation('onboarding');
  const [draft, setDraft] = useState(String(value));
  return (
    <div className="relative">
      <Input
        aria-label={t('review.priceAria')}
        type="number"
        inputMode="decimal"
        min={0}
        step="1"
        value={draft}
        disabled={disabled}
        className="ps-8"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const next = Number(draft);
          if (Number.isFinite(next) && next >= 0 && next !== value) onCommit(next);
          else setDraft(String(value));
        }}
      />
      <span className="pointer-events-none absolute inset-y-0 start-3 grid place-items-center text-xs text-fg-subtle">
        ₪
      </span>
    </div>
  );
}
